import { DT } from "../../constants.ts";
import { add, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyStatus, coneConstructs, damageBarrier, hitscan, isEnemy, moveProjectile, playersInRadius, spawnProjectile } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { FxKind } from "../../sim/events.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, contactEnemies, ownerAlive, ready, secs, spreadDir, startMove, stealth, trigger, useUlt, wishDir } from "../kitlib.ts";

export const V = { stealth: 0, shuriken: 1, ult: 2, dashTrail: 3, touched: 4 } as const;
export const PK = { Shuriken: pk(HeroId.Psyclone, 0) } as const;
export const FX = { Butterfly: fxk(HeroId.Psyclone, 0) } as const;

const PSI_DASH = MoveKind.Hero + 1;
const ULT_TICKS = 4 * 60;
const shurikenProjectile = {
  visual: { color: "#ff9cf5", size: 0.1, trail: true },
  pierce: 1,
  tick(sim, p) {
    const owner = sim.c.Projectile.owner[p]!;
    const hit = moveProjectile(sim, p);
    if (hit) {
      if (!ownerAlive(sim, owner)) return;
      if (hit.target !== undefined) {
        applyDamage(sim, hit.target, 10, owner, { pos: hit.pos });
        addOverhealth(sim, owner, Math.min(100, sim.c.Health.over[owner]! + 10), secs(5), owner);
      } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 10, owner);
      else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Pellet });
      const canPierce = hit.target !== undefined && sim.c.Projectile.bounces[p]! < 1;
      if (canPierce) {
        sim.c.Projectile.bounces[p] = sim.c.Projectile.bounces[p]! + 1;
        return;
      }
      sim.destroyEntity(p);
      return;
    }
    if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
  },
} satisfies NonNullable<HeroModule["projectiles"]>[number];

const def: HeroDef = {
  id: HeroId.Psyclone,
  slug: "psyclone",
  name: "Psyclone",
  role: Role.Damage,
  color: "#b04ad8",
  blurb: "A psychic infiltrator with cooldown-cutting bolts, recalled shuriken wings, stealth and a storm of blades.",
  health: 150,
  armor: 0,
  shields: 100,
  radius: 0.38,
  height: 1.74,
  headRadius: 0.21,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 16,
  reloadTime: 1.4,
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Mindbow", key: "LMB", description: "Fire a burst of psychic bolts; hits shorten ability cooldowns.", cooldown: 0 },
    [Slot.Secondary]: { name: "Wing Pins", key: "RMB", description: "Throw five shuriken, then press again to recall them through enemies.", cooldown: 8 },
    [Slot.Ability1]: { name: "Gale Blade", key: "Shift", description: "Dash forward, cutting enemies in your path.", cooldown: 12, charges: 2 },
    [Slot.Ability2]: { name: "Quiet Weather", key: "E", description: "Enter stealth for 2 seconds and move faster.", cooldown: 15 },
    [Slot.Ultimate]: { name: "Butterfly Spiral", key: "Q", description: "Spin for 4 seconds, repeatedly slashing nearby enemies.", cooldown: 0 },
  },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      for (const v of Object.values(V)) setHv(sim.c, eid, v, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      setHv(c, eid, V.stealth, c.Status.stealth[eid]! > 0 ? c.Status.stealth[eid]! / secs(2) : 0);
      setHv(c, eid, V.ult, c.Ult.active[eid]! > 0 ? c.Ult.active[eid]! / ULT_TICKS : 0);
      setHv(c, eid, V.dashTrail, c.Move.kind[eid] === PSI_DASH ? 1 : 0);
      if (hv(c, eid, V.shuriken) > 0) setHv(c, eid, V.shuriken, Math.max(0, hv(c, eid, V.shuriken) - DT));
      commonInput(sim, eid, input, pressed);
      if (c.Ult.active[eid]! > 0 && c.Ult.active[eid]! % 30 === 0) {
        const center = add(sim.posOf(eid), { x: 0, y: 1, z: 0 });
        sim.emit({ type: "explosion", pos: center, radius: 10, kind: FX.Butterfly });
        for (const { eid: t } of playersInRadius(sim, center, 10, (e) => isEnemy(sim, eid, e), false)) {
          applyStatus(sim, t, "slow", 0.35, { slowPct: 0.15, source: eid });
          applyDamage(sim, t, 160, eid);
        }
      }
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = ULT_TICKS;
        applyStatus(sim, eid, "phased", 0.5, { source: eid });
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        stealth(sim, eid, 2, { source: eid });
        applyStatus(sim, eid, "haste", 2, { hastePct: 0.5, source: eid });
        action(sim, eid, ActionKind.Cast, 12);
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        const d = wishDir(input);
        setHv(c, eid, V.touched, 0);
        startMove(sim, eid, PSI_DASH, secs(12 / 43), 43, { x: d.x * 43, y: 0, z: d.z * 43 });
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        setHv(c, eid, V.shuriken, 3);
        action(sim, eid, ActionKind.AltFire, 14);
        const a = aim(sim, eid);
        for (let i = 0; i < 5; i++) {
          const dir = spreadDir(a.dir, 0.86 * Math.PI / 180, c.Weapon.shots[eid]! * 31 + i * 11 + 5);
          spawnProjectile(sim, eid, { kind: PK.Shuriken, origin: add(a.origin, scale(dir, 0.5)), vel: scale(dir, 150), seconds: 25 / 150, radius: 0.08, seq: input.seq });
        }
      } else if ((pressed & Btn.Secondary) && hv(c, eid, V.shuriken) > 0) {
        action(sim, eid, ActionKind.AltFire, 12);
        for (const t of playersInRadius(sim, add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), 25, (e) => isEnemy(sim, eid, e), true)) applyDamage(sim, t.eid, 50, eid);
        setHv(c, eid, V.shuriken, 0);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = secs(0.6);
        action(sim, eid, ActionKind.Fire, 7);
        const a = aim(sim, eid);
        for (let i = 0; i < 4; i++) hitscan(sim, eid, a.origin, spreadDir(a.dir, 0.2 / 10, c.Weapon.shots[eid]! * 53 + i), { range: 35, damage: 12, falloffStart: 8, falloffEnd: 30, falloffMin: 0.7, canCrit: true, critMul: 1.5, fxKind: FxKind.Pistol, tracer: i === 0 });
      }
    },
    movement(sim, eid, _input, mods) {
      if (sim.c.Ult.active[eid]! > 0) mods.speedMul *= 0.85;
    },
    scripted(sim, eid, _input, kind) {
      if (kind !== PSI_DASH) return;
      sim.c.Vel.x[eid] = sim.c.Move.x[eid]!;
      sim.c.Vel.y[eid] = sim.c.Move.y[eid]!;
      sim.c.Vel.z[eid] = sim.c.Move.z[eid]!;
      contactEnemies(sim, eid, 1.1, (t) => {
        const bit = 1 << (sim.c.Net.id[t]! % 24);
        const seen = hv(sim.c, eid, V.touched);
        if ((seen & bit) !== 0) return;
        setHv(sim.c, eid, V.touched, seen | bit);
        applyDamage(sim, t, 50, eid);
      });
      for (const { eid: t } of coneConstructs(sim, eid, 1.8, 1.3)) damageConstruct(sim, t, 50, eid);
    },
    onDealtDamage(sim, attacker, _target, dealt) {
      if (dealt <= 0) return;
      for (const slot of [Slot.Secondary, Slot.Ability2, Slot.Ability1]) {
        const i = attacker * 6 + slot;
        sim.c.Abil.cd[i] = Math.max(0, sim.c.Abil.cd[i]! - secs(0.3));
      }
    },
  },
  projectiles: {
    [PK.Shuriken]: shurikenProjectile,
  },
  fx: { [FX.Butterfly]: { color: "#ff65f2" } },
};

export default module;
